import React, { useLayoutEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import industries from "../assets/img/industries-ban.png"

gsap.registerPlugin(ScrollTrigger);

const capabilities = [
  {
    number: "01",
    title: "Fully Automatic",
    highlight: "Square Bottom Paper Bags",
    description:
      "Advanced automated production for square bottom paper bags with twisted handles, delivering consistent dimensions, reliable construction, and high-volume production efficiency.",
    tag: "AUTOMATED PRODUCTION",
  },
  {
    number: "02",
    title: "Flexo Graphic",
    highlight: "Printing",
    description:
      "High-quality flexographic printing capabilities enable accurate branding, graphics, and custom designs across a wide range of paper packaging requirements.",
    tag: "PRINTING TECHNOLOGY",
  },
  {
    number: "03",
    title: "Material Handling",
    highlight: "& Storage",
    description:
      "Structured material handling and storage systems help maintain material integrity, production flow, traceability, and operational efficiency.",
    tag: "MATERIAL MANAGEMENT",
  },
  {
    number: "04",
    title: "Quality",
    highlight: "Assurance",
    description:
      "Stringent quality checks throughout the manufacturing process ensure consistency, durability, precision, and compliance with customer specifications.",
    tag: "QUALITY CONTROL",
  },
  {
    number: "05",
    title: "Export",
    highlight: "Capabilities",
    description:
      "Export-ready manufacturing and packaging operations enable us to serve customers across international markets with reliable and consistent supply.",
    tag: "GLOBAL DELIVERY",
  },
];

function ManufacturingCapabilities() {
  const sectionRef = useRef(null);
  const visualRef = useRef(null);
  const [active, setActive] = useState(0);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const intro = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
        },
      });

      intro
        .from(".manufacturing-eyebrow", {
          y: 25,
          opacity: 0,
          duration: 0.7,
        })
        .from(
          ".manufacturing-heading-line",
          {
            yPercent: 110,
            opacity: 0,
            duration: 0.9,
            stagger: 0.12,
            ease: "power4.out",
          },
          "-=0.3"
        )
        .from(
          ".manufacturing-intro",
          {
            y: 25,
            opacity: 0,
            duration: 0.7,
          },
          "-=0.5"
        )
        .from(
          ".manufacturing-visual",
          {
            scale: 0.9,
            opacity: 0,
            duration: 1,
            ease: "power3.out",
          },
          "-=0.5"
        )
        .from(
          ".capability-row",
          {
            x: -40,
            opacity: 0,
            duration: 0.7,
            stagger: 0.1,
          },
          "-=0.5"
        );

      // Rotating technical ring
      gsap.to(".technical-ring", {
        rotation: 360,
        duration: 30,
        repeat: -1,
        ease: "none",
      });

      // Floating green indicator
      gsap.to(".visual-indicator", {
        y: -8,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const changeCapability = (index) => {
    if (index === active) return;

    const content = visualRef.current;

    gsap.to(content, {
      opacity: 0,
      y: 15,
      duration: 0.2,
      onComplete: () => {
        setActive(index);

        gsap.fromTo(
          content,
          {
            opacity: 0,
            y: -15,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: "power3.out",
          }
        );
      },
    });
  };

  const current = capabilities[active];

  return (
    <div
      ref={sectionRef}
      className="manufacturing-section"
      id="manufacturing"
    >
      <div className="manufacturing-grid-bg" />
      <Container className="py-lg-5 px-lg-5" fluid>
        <div className="manufacturing-header">
          <div className="manufacturing-heading-grid">
            <h2 className="manufacturing-heading">
              {/* <span className="mfg-heading-mask">
                <span className="manufacturing-heading-line">
                  Built to manufacture.
                </span>
              </span> */}

              <span className="mfg-heading-mask">
                <span className="manufacturing-heading-line mfg-muted">
                  Engineered to perform.
                </span>
              </span>
            </h2>

            <p className="manufacturing-intro">
              Our manufacturing capabilities combine automation,
              technology, quality control, and operational discipline
              to deliver consistent packaging solutions at scale.
            </p>
          </div>
        </div>

        <div className="manufacturing-main">
          <div className="capability-list">
            {capabilities.map((capability, index) => (
              <button
                key={capability.number}
                className={`capability-row ${
                  active === index ? "active" : ""
                }`}
                onClick={() => changeCapability(index)}
              >
                <span className="capability-number">
                  {/* {capability.number} */}
                </span>

                <span className="capability-name">
                  {capability.title}
                  <strong>{capability.highlight}</strong>
                </span>

                <span className="capability-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="24" height="24">
  <path d="M14 50L50 14" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M27 14H50V37" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                </span>

                <span className="capability-active-line" />
              </button>
            ))}
          </div>

          <div className="manufacturing-visual">
            <div className="technical-ring">
              <span className="ring-point point-one" />
              <span className="ring-point point-two" />
              <span className="ring-point point-three" />
            </div>

            <div className="visual-grid" />

            <div className="machine-graphic">
              <div className="machine-top" />
              <div className="machine-body">
                <div className="machine-window">
                  <img src={industries} alt={industries} />
                  <span />
                  <span />
                  <span />
                </div>

                <div className="machine-conveyor">
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              </div>

              <div className="machine-base" />
            </div>

            <div className="visual-indicator">
              <span />
              PRODUCTION
            </div>

        
            <div ref={visualRef} className="visual-content">
              <span className="visual-category">
                {current.tag}
              </span>

              <div className="visual-number">
                {/* {current.number} */}
              </div>

              <h3>
                {current.title}
                <strong>{current.highlight}</strong>
              </h3>

              <p>{current.description}</p>
            </div>
          </div>
        </div>

      </Container>
    </div>
  );
}

export default ManufacturingCapabilities;